// Tree layout for the seed tree — one pure function, drawn twice: the
// extension's browse webview (vanilla SVG; the HTML only draws what it is
// handed) and the viz's LineageGraph (React). Both must agree on where every
// node and branch stub sits, so neither computes geometry itself.
//
// The model: seeds are commits with ONE parent; jams are branch refs anchored
// to a base seed and pointing at a tip seed. Depth is the row; siblings sit
// left-to-right by age; a parent centres over its subtree; a jam is a stub
// drawn just under its base with the tip marked on the tip node.

export interface TreeNodeInput {
  id: string;
  parentSeedId: string | null;
  /** ISO timestamp; siblings order by it. */
  createdAt: string;
}

export interface JamRefInput {
  id: string;
  baseSeedId: string;
  tipSeedId: string;
}

export interface LayoutMetrics {
  nodeW: number;
  nodeH: number;
  colGap: number;
  rowGap: number;
}

export interface PlacedNode {
  id: string;
  /** Left edge. */
  x: number;
  /** Top edge. */
  y: number;
  depth: number;
  /** Jams whose tip is this node, in the order they were given. */
  tipOf: string[];
}

export interface PlacedJam {
  id: string;
  baseSeedId: string;
  tipSeedId: string;
  /** Left edge — aligned with the base node. */
  x: number;
  /** Top edge — inside the row gap under the base node. */
  y: number;
  /** Stub height; several stubs on one base stack downward. */
  h: number;
}

export interface TreeEdge {
  from: string;
  to: string;
}

export interface TreeLayout {
  nodes: PlacedNode[];
  edges: TreeEdge[];
  jams: PlacedJam[];
  width: number;
  height: number;
}

/** Height of one jam stub as a fraction of node height. */
const STUB_H_RATIO = 0.4;

export function layoutTree(
  nodes: TreeNodeInput[],
  jams: JamRefInput[],
  m: LayoutMetrics,
): TreeLayout {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const children = new Map<string, TreeNodeInput[]>();
  const roots: TreeNodeInput[] = [];
  for (const n of nodes) {
    // A parent outside the set (pending, deleted, or beyond a cap) makes this
    // node a root of its own — drawn, never dropped.
    const parent = n.parentSeedId !== null && byId.has(n.parentSeedId) ? n.parentSeedId : null;
    if (parent === null) roots.push(n);
    else children.set(parent, [...(children.get(parent) ?? []), n]);
  }
  const byAge = (a: TreeNodeInput, b: TreeNodeInput): number =>
    a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : a.id < b.id ? -1 : 1;
  roots.sort(byAge);
  for (const list of children.values()) list.sort(byAge);

  // Jams whose base is missing cannot be anchored and are dropped; a jam whose
  // tip is missing still gets its stub (the tip may be pending) — no marker.
  const stubsByBase = new Map<string, JamRefInput[]>();
  const tipOf = new Map<string, string[]>();
  for (const j of jams) {
    if (!byId.has(j.baseSeedId)) continue;
    stubsByBase.set(j.baseSeedId, [...(stubsByBase.get(j.baseSeedId) ?? []), j]);
    if (byId.has(j.tipSeedId)) tipOf.set(j.tipSeedId, [...(tipOf.get(j.tipSeedId) ?? []), j.id]);
  }

  const stubH = Math.round(m.nodeH * STUB_H_RATIO);
  const colW = m.nodeW + m.colGap;

  // Rows are not a fixed pitch. Stubs hang into the gap under their base, and
  // a base with several jams stacks them past a single gap and into the row
  // below — so each depth's gap grows to clear the tallest stack on it. One
  // jam still fits the plain rowGap, which is why every existing layout is
  // unchanged; only a depth that actually carries two or more opens up.
  const stubTop = Math.round(m.rowGap / 4);
  const stackH = (count: number): number =>
    count === 0 ? 0 : stubTop + count * stubH + (count - 1) * 2;

  const depthOf = new Map<string, number>();
  {
    const stack: Array<{ n: TreeNodeInput; d: number }> = roots.map((r) => ({ n: r, d: 0 }));
    while (stack.length > 0) {
      const { n, d } = stack.pop()!;
      depthOf.set(n.id, d);
      for (const k of children.get(n.id) ?? []) stack.push({ n: k, d: d + 1 });
    }
  }
  const stackAtDepth = new Map<number, number>();
  for (const [id, d] of depthOf) {
    const need = stackH((stubsByBase.get(id) ?? []).length);
    if (need > (stackAtDepth.get(d) ?? 0)) stackAtDepth.set(d, need);
  }
  /** Top edge of each row, gaps widened where stubs need the room. */
  const rowY: number[] = [0];
  for (let d = 0; d < depthOf.size; d++) {
    const need = stackAtDepth.get(d) ?? 0;
    const gap = Math.max(m.rowGap, need + Math.round(m.rowGap / 3));
    rowY.push(rowY[d]! + m.nodeH + gap);
  }
  const placed: PlacedNode[] = [];
  const edges: TreeEdge[] = [];
  const stubs: PlacedJam[] = [];
  let nextCol = 0;
  let maxDepth = 0;
  let maxBottom = 0;

  // Post-order: leaves claim columns; a parent centres over its children.
  const place = (n: TreeNodeInput, depth: number): number => {
    const kids = children.get(n.id) ?? [];
    let x: number;
    if (kids.length === 0) {
      x = nextCol * colW;
      nextCol++;
    } else {
      const xs = kids.map((k) => place(k, depth + 1));
      x = (xs[0]! + xs[xs.length - 1]!) / 2;
    }
    const y = rowY[depth]!;
    placed.push({ id: n.id, x, y, depth, tipOf: tipOf.get(n.id) ?? [] });
    for (const k of kids) edges.push({ from: n.id, to: k.id });
    maxDepth = Math.max(maxDepth, depth);
    maxBottom = Math.max(maxBottom, y + m.nodeH);

    const mine = stubsByBase.get(n.id) ?? [];
    mine.forEach((j, i) => {
      const sy = y + m.nodeH + stubTop + i * (stubH + 2);
      stubs.push({ id: j.id, baseSeedId: j.baseSeedId, tipSeedId: j.tipSeedId, x, y: sy, h: stubH });
      maxBottom = Math.max(maxBottom, sy + stubH);
    });
    return x;
  };
  for (const r of roots) place(r, 0);

  // Emit in the input order so callers can zip against their own arrays.
  const order = new Map(nodes.map((n, i) => [n.id, i]));
  placed.sort((a, b) => order.get(a.id)! - order.get(b.id)!);
  edges.sort((a, b) => order.get(a.to)! - order.get(b.to)!);

  return {
    nodes: placed,
    edges,
    jams: stubs,
    width: nextCol === 0 ? 0 : nextCol * colW - m.colGap,
    height: maxBottom,
  };
}
